/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { nativeFetchRef } from '@amazon/vinyl-util'
import { MARKETPLACE_TLDS } from '@/territory'
import { IS_TAURI } from '@/util/platform'
import { installShellNetworkState } from '@/offline/shellNetworkState'
import {
    createShellFetch,
    type Fetch,
    limitConcurrency,
    type ShellBridge,
    shellHasNativeFetch,
} from '@/service/shellFetch'

// The WebView enforces CORS for the page's origin (`app://localhost`, `http://localhost:8083`
// under `tauri dev`), so a host that refuses it is fetched by the shell instead, from Rust.
// Each host's preflight from that origin, with the headers the app sends:
//
// | Host                                                 | CORS for the app's origin               | Fetched by |
// | ---------------------------------------------------- | --------------------------------------- | ---------- |
// | gql.music.amazon.dev                                 | `*`, every requested header             | WebView    |
// | log.vinyl.music.amazon.dev, log.dev.vinyl...         | `*`                                     | WebView    |
// | music.amazon.<tld> (Stratus, DMLS, telemetry)        | Stratus preflight 500, no allow-origin  | shell      |
// | api.amazon.<tld>                                     | allows only `content-type`              | shell      |
// | alpha.resonance.music.amazon.dev                     | credentials, no allow-origin            | shell      |
// | purchase.fan / gateway.fan.amazon.com (credentialed) | credentialed; the shell sends no cookie | shell      |
// | other amazon.<tld>, *.amazon.dev, localhost          | unverified                              | shell      |
// | media CDNs, podcast hosts, the app's own origin      | `*`, or same origin                     | WebView    |
//
// The shell's `native_fetch` keeps one pooled client; an older native build has only the HTTP
// plugin, whose client (so connection) is new for every request. Only the plugin's capability
// scope (`packages/bf-tauri/src-tauri/capabilities/default.json`, kept in step with the marketplace
// TLDs by a unit test) reaches the shell, and reqwest speaks only http/https.

const WEBVIEW_CORS_HOSTS: readonly string[] = [
    'gql.music.amazon.dev',
    'log.vinyl.music.amazon.dev',
    'log.dev.vinyl.music.amazon.dev',
]

/** Requests the shell fetches at once; the rest queue, telemetry last. */
const SHELL_CONCURRENCY = 6

export async function initializeTauri() {
    if (!IS_TAURI) return
    installShellNetworkState()
    const shellFetch = limitConcurrency(await nativeTransport(), SHELL_CONCURRENCY, isTelemetry)
    const webviewFetch = window.fetch.bind(window)
    nativeFetchRef.set(
        () => (input: RequestInfo | URL, init?: RequestInit) =>
            usesShell(input) ? shellFetch(input, init) : webviewFetch(input, init)
    )
}

// Dynamic imports keep the Tauri runtime in its own chunk, out of the web entry.
async function nativeTransport(): Promise<Fetch> {
    const { invoke, Channel } = await import('@tauri-apps/api/core')
    const bridge: ShellBridge = { invoke, Channel }
    if (await shellHasNativeFetch(bridge)) return createShellFetch(bridge)
    return (await import('@tauri-apps/plugin-http')).fetch
}

function isMarketplaceHost(host: string): boolean {
    return MARKETPLACE_TLDS.some(
        (tld) => host === `amazon.${tld}` || host.endsWith(`.amazon.${tld}`)
    )
}

function usesShell(input: RequestInfo | URL): boolean {
    const url = parseUrl(input)
    if (!url || url.origin === window.location.origin) return false
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
    const host = url.hostname
    if (WEBVIEW_CORS_HOSTS.includes(host)) return false
    return host === 'localhost' || host.endsWith('.amazon.dev') || isMarketplaceHost(host)
}

function isTelemetry(input: RequestInfo | URL): boolean {
    const url = parseUrl(input)
    return !!url && /^(\/ZAZ)?\/telemetry\//.test(url.pathname)
}

function parseUrl(input: RequestInfo | URL): URL | null {
    try {
        if (input instanceof URL) return input
        return new URL(typeof input === 'string' ? input : input.url, window.location.href)
    } catch {
        return null
    }
}
